import React, {useEffect} from 'react';

import {sendDesktopAnalyticsPerSession} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {Tooltip} from '@optimaxdev/design-system';
import {FastTruckMini, Icon, Question} from '@optimaxdev/icons';
import {getCorrectDayTwoDayDelivery} from '@optimaxdev/utils';
import AnimateHeight from 'react-animate-height';

import {useFeature} from 'components/feature';
import {CART_ONE_DAY_SHIPPING} from 'constants/analytics/events/cart';
import {GENERAL_EXPERIMENT_INDICATION} from 'constants/analytics/events/general';

import {useGetIsCartEligibleForNextDayShipping} from './utils/useGetIsCartEligibleForNextDayShipping';

import s from './nextDayShippingBanner.scss';

/**
 * Next Day Shipping Banner
 */
export const NextDayShippingBanner = () => {
    const {data: isCartEligibleForNextDayData} = useGetIsCartEligibleForNextDayShipping();
    const isCartTwoDayDelivery = useFeature('cartTwoDayDelivery');
    const isCartEligible = isCartEligibleForNextDayData?.isCartEligibleForNextDayShipping;
    const correctDayDelivery =
        isCartEligibleForNextDayData?.additionalData &&
        getCorrectDayTwoDayDelivery(isCartEligibleForNextDayData?.additionalData?.day);

    useEffect(() => {
        if (isCartEligible && correctDayDelivery) {
            sendDesktopAnalyticsPerSession({
                event: GENERAL_EXPERIMENT_INDICATION,
                isInteraction: false,
                label: {
                    expName: 'cartTwoDayDelivery',
                    label: `${isCartTwoDayDelivery}`,
                },
            });
            sendDesktopAnalyticsPerSession({
                event: CART_ONE_DAY_SHIPPING,
                isInteraction: false,
                label: `1BD Shipping - ${
                    correctDayDelivery === 'tomorrow' ? 'Tomorrow' : 'Other'
                } - Eligible`,
            });
            if (!isCartTwoDayDelivery) return;
            sendDesktopAnalyticsPerSession({
                event: CART_ONE_DAY_SHIPPING,
                isInteraction: false,
                label: `1BD Shipping - ${
                    correctDayDelivery === 'tomorrow' ? 'Tomorrow' : 'Other'
                } - Showed`,
            });
        }
    }, [isCartTwoDayDelivery, isCartEligible, correctDayDelivery]);

    const handleOpenToolTip = (value: boolean) => {
        if (value)
            sendDesktopAnalyticsPerSession({
                event: CART_ONE_DAY_SHIPPING,
                isInteraction: true,
                label: `${
                    correctDayDelivery === 'tomorrow' ? 'Tomorrow' : 'Other'
                } - Learn More - open`,
            });
    };

    const getTooltipContent = () => {
        if (correctDayDelivery !== 'tomorrow')
            return (
                <p className={s.text}>
                    The items in your cart qualify for next-day delivery! You can get your order in
                    up to 2 business days with faster production and faster delivery, plus a
                    money-back guarantee on the shipping charges if it doesn’t arrive on time.
                    <br />
                    <br />
                    Example: If you place an order on Mon. after 12 PM ET, it will arrive by
                    Wednesday at the latest. Please note, during the holiday season & peak shopping
                    days, shipping delays may occur.
                </p>
            );
        return (
            <p className={s.text}>
                The items in your cart qualify for next-day delivery! You can get your order by
                tomorrow with faster production and faster delivery, plus a money-back guarantee on
                the shipping charges if it doesn’t arrive on time.
                <br />
                <br />
                Orders must be placed from Monday to Friday before 12PM ET to be eligible. Please
                note, during the holiday season & peak shopping days, shipping delays may occur.
            </p>
        );
    };
    if (!isCartTwoDayDelivery || !correctDayDelivery) return null;
    return (
        <AnimateHeight height={isCartEligible ? 'auto' : 0}>
            <div className={s.container}>
                <span className={s.shippingIcon}>
                    <Icon icon={FastTruckMini} size="responsive" strokeWidth={1.2} />
                </span>
                <p className={s.title}>
                    Your order is eligible for Next-Day Delivery. Order now for delivery{' '}
                    <b>by {correctDayDelivery}</b>!
                </p>
                <Tooltip
                    content={<div className={s.content}>{getTooltipContent()}</div>}
                    onVisibilityChanged={handleOpenToolTip}
                    position="bottom"
                >
                    <span className={s.tooltip}>
                        <Icon icon={Question} size="small" />
                    </span>
                </Tooltip>
            </div>
        </AnimateHeight>
    );
};
